/**
 * Paths to Firestore Format Converter
 *
 * Converts CompositeDataPicker paths to Firestore sourceData format.
 * Used when saving Strategy/Block to extract entity IDs from selection paths.
 *
 * Input format:  ['@audience[ABC].actors[DEF].fears', '@task[XYZ].name']
 * Output format: { audiences: ['ABC'], tasks: ['XYZ'] }
 */

/**
 * Firestore sourceData format for Strategy/Block entities
 *
 * Index signature added for compatibility with Record<string, unknown>
 * (required by Firebase service methods)
 */
export interface FirestoreSourceData {
  /** Audience IDs */
  audiences: string[];

  /** Task IDs */
  tasks: string[];

  /** Variable IDs */
  variableIds: string[];

  /**
   * Insight-derived variable IDs (Task 9, AC-019). A SEPARATE plane from
   * `variableIds`: project variables and accepted insight-derived variables
   * are picked in two independent pickers and resolved from two sources.
   */
  insightVariableIds: string[];

  /** StudyCase IDs */
  studyCases: string[];

  /** Review IDs */
  reviews: string[];

  /** Rating IDs */
  ratings: string[];

  /** BigNumber IDs */
  bigNumbers: string[];

  /** InsightsAnalysis IDs */
  insightIds: string[];

  /**
   * W13 (IS1, AC-W13-07): the THESIS ids the picker ticked
   * (`@insight[a].<category>[<itemId>]`), a separate plane from `insightIds`,
   * which stays the analysis-level contract. Empty = no per-thesis narrowing.
   */
  insightThesisIds: string[];

  /**
   * W13 (IS1): categories ticked WHOLE (`@insight[a].<category>`). The picker's
   * path optimisation collapses a fully selected category into one path that
   * carries no thesis ids, so the category travels as a marker and the server
   * resolves it into every thesis of that category.
   */
  insightCategories: string[];

  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: string[];
}

/**
 * `@insight[<analysisId>]` · `@insight[<analysisId>].<category>` ·
 * `@insight[<analysisId>].<category>[<insightId>]` (optionally with the W13
 * evidence suffix `.sources[<originalItemId>]`, which still addresses that
 * thesis) — the levels the insight picker emits. A bare analysis path means
 * «the whole analysis» and contributes NO thesis id (AC-W13-07).
 *
 * Final review FR-5 — the pattern is `$`-ANCHORED and its category class is the
 * closed `[^[\].]+` of the citation grammar (ruling W13-R28, `pathParser.ts` /
 * `insightCitationPath.ts` / `insightImpact.ts`). Unanchored, with a category class
 * that allowed everything but brackets, `@insight[a].fears.sources[item-9]` — a
 * suffix hung on the CATEGORY, with no thesis — matched its prefix and was read as
 * «the whole category fears», i.e. every thesis of it. That path is rejected by
 * `routePaths` today, so nothing produced it; the two spellings simply must not be
 * able to drift apart.
 */
const INSIGHT_PATH = /^@insight\[([^\]]+)\](?:\.([^[\].]+)(?:\[([^\]]+)\](?:\.sources\[([^\]]+)\])?)?)?$/;

/** The scope key of the cross-task insight package (ruling W13-R22). */
export const GLOBAL_INSIGHT_SCOPE = 'global';

/** W13 (IS1): the thesis selection a set of picker paths expresses. */
export interface InsightSelectionFromPaths {
  insightIds?: string[];
  categories?: string[];
  /** Ruling W13-R22: the scopes these paths address. Always `['global']` here. */
  scopes?: string[];
}

/**
 * Reads the picker paths as an insight SELECTION.
 *
 * `analysisId` scopes the read to one analysis — the create/edit dialog pins
 * exactly one, and theses of any other analysis in the path list would be
 * refused by the server as foreign ids (AC-W13-21). Returns `undefined` when
 * nothing narrows: a bare `@insight[a]`, no insight path at all, or a picker
 * state where every level collapsed to the analysis root — all of which mean
 * «every thesis», i.e. today's projection.
 */
export function insightSelectionFromPaths(
  paths: string[],
  analysisId?: string,
): InsightSelectionFromPaths | undefined {
  const insightIds: string[] = [];
  const categories: string[] = [];
  for (const path of paths) {
    const match = INSIGHT_PATH.exec(path);
    if (!match) continue;
    const [, pathAnalysisId, category, itemId] = match;
    if (analysisId && pathAnalysisId !== analysisId) continue;
    if (itemId) {
      if (!insightIds.includes(itemId)) insightIds.push(itemId);
    } else if (category) {
      if (!categories.includes(category)) categories.push(category);
    }
  }
  if (insightIds.length === 0 && categories.length === 0) return undefined;
  return {
    ...(insightIds.length ? { insightIds } : {}),
    ...(categories.length ? { categories } : {}),
    // Ruling W13-R22: this picker hydrates `category_packages` ONLY, so every id
    // it can emit belongs to the GLOBAL namespace. Saying so keeps the per-task
    // packages — a disjoint id space — on their default projection instead of
    // making every task look starved.
    scopes: [GLOBAL_INSIGHT_SCOPE],
  };
}

/**
 * Converts CompositeDataPicker paths to Firestore sourceData format
 *
 * Extracts unique entity IDs from paths in the format:
 * - @audience[ID]...
 * - @task[ID]...
 *
 * @param paths - Array of selection paths from CompositeDataPicker
 * @returns FirestoreSourceData object with unique entity IDs
 *
 * @example
 * ```typescript
 * const paths = [
 *   '@audience[aud-1].actors[actor-1].fears',
 *   '@audience[aud-1].actors[actor-2].coreJobs',
 *   '@task[task-1].name',
 *   '@task[task-1].description'
 * ];
 *
 * const result = pathsToFirestoreFormat(paths);
 * // { audiences: ['aud-1'], tasks: ['task-1'] }
 * ```
 */
export function pathsToFirestoreFormat(paths: string[]): FirestoreSourceData {
  const audienceIds = new Set<string>();
  const taskIds = new Set<string>();
  const variableIds = new Set<string>();
  const insightVariableIds = new Set<string>();
  const reviewIds = new Set<string>();
  const ratingIds = new Set<string>();
  const studyCaseIds = new Set<string>();
  const bigNumberIds = new Set<string>();
  const insightIds = new Set<string>();
  const insightThesisIds = new Set<string>();
  const insightCategories = new Set<string>();

  for (const path of paths) {
    const audienceMatch = path.match(/@audience\[([^\]]+)\]/);
    if (audienceMatch) {
      audienceIds.add(audienceMatch[1]);
    }

    const taskMatch = path.match(/@task\[([^\]]+)\]/);
    if (taskMatch) {
      taskIds.add(taskMatch[1]);
    }

    // Insight-derived FIRST: '@insightVariable[' must never be read as a
    // project '@variable[' path (the prefixes differ, the intent does not).
    const insightVariableMatch = path.match(/@insightVariable\[([^\]]+)\]/);
    if (insightVariableMatch) {
      insightVariableIds.add(insightVariableMatch[1]);
      continue;
    }

    const variableMatch = path.match(/@variable\[([^\]]+)\]/);
    if (variableMatch) {
      variableIds.add(variableMatch[1]);
    }

    const reviewMatch = path.match(/@review\[([^\]]+)\]/);
    if (reviewMatch) {
      reviewIds.add(reviewMatch[1]);
    }

    const ratingMatch = path.match(/@rating\[([^\]]+)\]/);
    if (ratingMatch) {
      ratingIds.add(ratingMatch[1]);
    }

    const studyCaseMatch = path.match(/@studyCase\[([^\]]+)\]/);
    if (studyCaseMatch) {
      studyCaseIds.add(studyCaseMatch[1]);
    }

    const bigNumberMatch = path.match(/@bigNumber\[([^\]]+)\]/);
    if (bigNumberMatch) {
      bigNumberIds.add(bigNumberMatch[1]);
    }

    // W13 (IS1, AC-W13-07): the analysis id stays the pinned-analysis contract,
    // and the level BELOW it — a thesis or a whole category — becomes the
    // selection. A bare `@insight[a]` contributes neither: it means «all».
    //
    // FR-5: the ANALYSIS id is read by the loose entity pattern every other entity
    // here uses, so a path whose deeper levels this module does not understand still
    // pins its analysis; only the SELECTION is read through the closed grammar, where
    // a malformed level must contribute nothing rather than a phantom category.
    const analysisMatch = path.match(/^@insight\[([^\]]+)\]/);
    if (analysisMatch) insightIds.add(analysisMatch[1]);
    const insightMatch = INSIGHT_PATH.exec(path);
    if (insightMatch) {
      const [, , category, itemId] = insightMatch;
      if (itemId) insightThesisIds.add(itemId);
      else if (category) insightCategories.add(category);
    }
  }

  return {
    audiences: Array.from(audienceIds),
    tasks: Array.from(taskIds),
    variableIds: Array.from(variableIds),
    insightVariableIds: Array.from(insightVariableIds),
    reviews: Array.from(reviewIds),
    ratings: Array.from(ratingIds),
    studyCases: Array.from(studyCaseIds),
    bigNumbers: Array.from(bigNumberIds),
    insightIds: Array.from(insightIds),
    insightThesisIds: Array.from(insightThesisIds),
    insightCategories: Array.from(insightCategories),
  };
}

/**
 * Extracts entity IDs for AI webhook payload
 *
 * Similar to pathsToFirestoreFormat but returns a structure
 * compatible with AIPayloadBuilder expectations.
 *
 * @param paths - Array of selection paths from CompositeDataPicker
 * @returns Object with typed ID arrays for AI payload
 */
export function pathsToAiSourceDataIds(paths: string[]): {
  audienceIds: string[];
  taskIds: string[];
  studyCaseIds: string[];
  reviewIds: string[];
  ratingIds: string[];
  bigNumberIds: string[];
  insightIds: string[];
} {
  const firestoreData = pathsToFirestoreFormat(paths);

  return {
    audienceIds: firestoreData.audiences,
    taskIds: firestoreData.tasks,
    studyCaseIds: firestoreData.studyCases ?? [],
    reviewIds: firestoreData.reviews ?? [],
    ratingIds: firestoreData.ratings ?? [],
    bigNumberIds: firestoreData.bigNumbers ?? [],
    insightIds: firestoreData.insightIds ?? [],
  };
}
